/**
 * HeroBanner — Blinkit-style rotating promo carousel for the customer home.
 *
 * IMPORTANT: This component does NOT use Tailwind. Tailwind is declared in
 * package.json but is not compiled in this CRA build (no tailwind.config,
 * no postcss.config, no @tailwind directives). Use plain CSS / inline styles.
 *
 * - Auto-advances every 5s, pauses on hover.
 * - Swipeable on touch via Framer Motion drag.
 * - Click forwards to the slide's CTA route (or external URL).
 * - Always renders a visible CSS gradient background even when no image is set.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './HeroBanner.css';

export interface HeroSlide {
  id: string | number;
  title: string;
  subtitle?: string;
  cta?: string;
  ctaHref?: string;
  image?: string;
  /** A real CSS background value (gradient or color). Required for visibility. */
  background?: string;
}

const DEFAULT_SLIDES: HeroSlide[] = [
  {
    id: 'free-delivery',
    title: 'Free delivery on your first order',
    subtitle: 'Delivered in 10 minutes — fresh, every time',
    cta: 'Shop now',
    ctaHref: '/catalog',
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #14b8a6 100%)',
  },
  {
    id: 'fruits-veg',
    title: 'Fresh fruits & vegetables',
    subtitle: 'Up to 30% off — straight from the farm',
    cta: 'Browse produce',
    ctaHref: '/catalog?category=fruits-vegetables',
    background: 'linear-gradient(90deg, #fbbf24 0%, #fb923c 50%, #f43f5e 100%)',
  },
  {
    id: 'household',
    title: 'Refill your household essentials',
    subtitle: 'Detergents, cleaners & more',
    cta: 'Stock up',
    ctaHref: '/catalog?category=household',
    background: 'linear-gradient(90deg, #0ea5e9 0%, #6366f1 50%, #8b5cf6 100%)',
  },
];

interface Props {
  slides?: HeroSlide[];
  intervalMs?: number;
}

export const HeroBanner: React.FC<Props> = ({ slides = DEFAULT_SLIDES, intervalMs = 5000 }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(id);
  }, [paused, slides.length, intervalMs]);

  if (!slides.length) return null;
  const slide = slides[idx];
  const bg = slide.background || 'linear-gradient(90deg, #10b981 0%, #14b8a6 100%)';

  const handleCta = () => {
    if (!slide.ctaHref) return;
    if (/^https?:\/\//.test(slide.ctaHref)) window.location.href = slide.ctaHref;
    else navigate(slide.ctaHref);
  };

  return (
    <div
      className="hb-root"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Promotional banners"
      data-testid="hero-banner"
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={slide.id}
          className="hb-slide"
          // Pass the background via a CSS custom property so framer-motion's
          // per-frame style updates don't clobber it (motion only re-applies
          // properties it animates; CSS variables survive untouched and the
          // .hb-slide class consumes the var).
          style={{ ['--hb-bg' as string]: bg } as React.CSSProperties}
          data-testid="hero-slide"
          data-bg={bg}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
            if (info.offset.x < -60) setIdx((i) => (i + 1) % slides.length);
            else if (info.offset.x > 60) setIdx((i) => (i - 1 + slides.length) % slides.length);
          }}
        >
          {slide.image && (
            <img src={slide.image} alt="" className="hb-slide-img" loading="eager" />
          )}
          <div className="hb-slide-shade" />
          <div className="hb-content">
            <h2 className="hb-title">{slide.title}</h2>
            {slide.subtitle && <p className="hb-subtitle">{slide.subtitle}</p>}
            {slide.cta && (
              <button onClick={handleCta} className="hb-cta" type="button">
                {slide.cta} →
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {slides.length > 1 && (
        <div className="hb-dots" role="tablist" aria-label="Slide selector">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-selected={i === idx}
              role="tab"
              onClick={() => setIdx(i)}
              className={`hb-dot${i === idx ? ' is-active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroBanner;

